import React, { memo, useCallback, useRef, useState } from 'react';

import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { DocLinkIcon, FileAddIcon } from '@sharedCR/coreDesignSystem/icons';
import TextFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/form/TextFieldCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { KEY_CODES } from 'constants/keyboard';

import {
  TEAM_CHAT_ATTACHMENT_MAX_BYTES,
  TEAM_CHAT_ATTACHMENT_MAX_MB,
  TEAM_CHAT_INPUT_PLACEHOLDER,
  TEAM_CHAT_SHARE_VIEW_TOOLTIP,
} from '../TeamChat.constants';
import {
  buildEntityAlias,
  convertAliasesToTokens,
  replaceEntityQueryWithAlias,
} from '../TeamChat.entityAlias.utils';
import {
  getEntityQueryAtCaret,
  getEntityReferenceKey,
  sanitizeEntityLabel,
} from '../TeamChat.entityReferences.utils';
import {
  getCurrentViewLink,
  getMemberId,
  getMentionQueryAtCaret,
  getSlashCommandId,
  getSlashCommandQueryAtStart,
  replaceMentionAtCaret,
  replaceSlashCommandAtStart,
} from '../TeamChat.utils';
import TeamChatEntitySuggestions from '../TeamChatEntitySuggestions';
import TeamChatMentionSuggestions from '../TeamChatMentionSuggestions';
import TeamChatSlashCommandSuggestions from '../TeamChatSlashCommandSuggestions';

import TeamChatComposerReferencePreview from './components/TeamChatComposerReferencePreview';
import TeamChatSoftLinkifyHint from './components/TeamChatSoftLinkifyHint';
import useChatDraft from './hooks/useChatDraft';
import useChatEntityAttachment from './hooks/useChatEntityAttachment';
import useChatEntityPaste from './hooks/useChatEntityPaste';
import useChatEntityReferences from './hooks/useChatEntityReferences';
import useChatMentions from './hooks/useChatMentions';
import useChatSlashCommands from './hooks/useChatSlashCommands';
import useChatSoftLinkify from './hooks/useChatSoftLinkify';
import useChatTyping from './hooks/useChatTyping';
import {
  hiddenFileInputStyles,
  inputStyles,
  rootStyles,
  rowStyles,
} from './TeamChatMessageInput.styles';
import { TeamChatMessageInputTestIds } from './TeamChatMessageInput.testIds';

const TeamChatMessageInput = memo(
  ({
    chatId = null,
    members = [],
    currentUserId = null,
    priorityMemberIds = null,
    isDisabled = false,
    onSendMessage,
    onUploadAttachment = null,
    onStartTyping = null,
    onStopTyping = null,
    canShareCurrentView = false,
    placeholder = TEAM_CHAT_INPUT_PLACEHOLDER,
    replyToId = null,
  }) => {
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    // Draft-scoped resolution hint: sanitized label -> { entityType, entityId } for every reference
    // inserted as a `#<label>` alias. It is a HINT for send-time token conversion — if it is lost
    // (e.g. a reloaded draft) conversion degrades to a unique candidate-label match, so it never
    // needs to persist beyond the draft lifecycle. Cleared on send.
    const aliasMapRef = useRef({});

    const { draft, setDraft, clearDraft } = useChatDraft(chatId);
    const [text, setText] = useState(draft);

    const { notifyTyping, flushStopTyping } = useChatTyping({ onStartTyping, onStopTyping });

    const {
      mentionRange,
      mentionQuery,
      suggestions,
      focusedMemberId,
      setFocusedMemberId,
      updateFromCaret: updateMentionFromCaret,
      reset: resetMentions,
    } = useChatMentions({ members, currentUserId, priorityMemberIds });

    const {
      slashRange,
      slashSuggestions,
      focusedCommandId,
      setFocusedCommandId,
      updateFromCaret: updateSlashFromCaret,
      reset: resetSlashCommands,
    } = useChatSlashCommands();

    const {
      entityRange,
      entityQuery,
      groups: entityGroups,
      flatItems: entityFlatItems,
      focusedEntityKey,
      setFocusedEntityKey,
      updateFromCaret: updateEntityFromCaret,
      reset: resetEntities,
      dismiss: dismissEntities,
      isLoading: isEntityDataLoading,
      isCandidatesLoaded: isEntityCandidatesLoaded,
      candidatesByType: entityCandidatesByType,
    } = useChatEntityReferences({ chatId });

    const recordAlias = useCallback(({ entityType, entityId, label }) => {
      const aliasLabel = sanitizeEntityLabel(label);

      if (!aliasLabel) {
        return;
      }

      // Two DIFFERENT entities sharing one label make that label ambiguous — a null entry
      // excludes it from alias-map conversion so it degrades to plain text instead of silently
      // linking both occurrences to whichever entity was inserted last. Once a label collides
      // it stays ambiguous for the rest of the draft (re-inserting one of the entities must not
      // resurrect a single winner while both aliases are still in the text).
      const hasEntry = aliasLabel in aliasMapRef.current;
      const existing = aliasMapRef.current[aliasLabel];
      const isCollision =
        hasEntry &&
        (_.isNil(existing) || existing.entityType !== entityType || existing.entityId !== entityId);

      aliasMapRef.current = {
        ...aliasMapRef.current,
        [aliasLabel]: isCollision ? null : { entityType, entityId },
      };
    }, []);

    const resolveEntityPaste = useChatEntityPaste({ chatId });

    const {
      match: softLinkifyMatch,
      updateFromCaret: updateSoftLinkifyFromCaret,
      dismiss: dismissSoftLinkify,
      reset: resetSoftLinkify,
    } = useChatSoftLinkify({ chatId });

    const updateMentionStateFromCaret = useCallback(
      (value, caret) => {
        updateMentionFromCaret(value, caret);
        updateSlashFromCaret(value, caret);
        // The @ / and # triggers are mutually exclusive — the entity popup never opens while a
        // mention or slash-command popup is (or would be) active at this caret position.
        const isMentionActive = !!getMentionQueryAtCaret(value, caret);
        const isSlashActive = !!getSlashCommandQueryAtStart(value, caret);
        const isEntityActive = !!getEntityQueryAtCaret(value, caret);

        updateEntityFromCaret(value, caret, {
          isSuppressed: isMentionActive || isSlashActive,
        });
        // The soft-linkify hint yields to every explicit trigger popup — it only offers while
        // typing pure plain text with no @/slash/# query at the caret.
        updateSoftLinkifyFromCaret(value, caret, {
          isSuppressed: isMentionActive || isSlashActive || isEntityActive,
        });
      },
      [
        updateMentionFromCaret,
        updateSlashFromCaret,
        updateEntityFromCaret,
        updateSoftLinkifyFromCaret,
      ],
    );

    const updateMentionStateFromInputEvent = useCallback(
      e => {
        const value = e.target?.value ?? '';
        const caret = e.target?.selectionStart ?? value.length;

        updateMentionStateFromCaret(value, caret);
      },
      [updateMentionStateFromCaret],
    );

    const onChange = useCallback(
      e => {
        const { value } = e.target;

        setText(value);
        setDraft(value);
        updateMentionStateFromInputEvent(e);
        notifyTyping(value.trim());
      },
      [setDraft, updateMentionStateFromInputEvent, notifyTyping],
    );

    const onMentionSelect = useCallback(
      member => {
        if (!member || !mentionRange) {
          return;
        }

        const result = replaceMentionAtCaret({ text, mentionRange, member });

        setText(result.text);
        resetMentions();

        setTimeout(() => {
          const input = inputRef.current;

          if (!input) {
            return;
          }

          input.focus();
          input.setSelectionRange(result.caret, result.caret);
        }, 0);
      },
      [text, mentionRange, resetMentions],
    );

    const onSlashCommandSelect = useCallback(
      command => {
        if (!command || !slashRange) {
          return;
        }

        const result = replaceSlashCommandAtStart({ text, commandRange: slashRange, command });

        setText(result.text);
        setDraft(result.text);
        resetSlashCommands();

        setTimeout(() => {
          const input = inputRef.current;

          if (!input) {
            return;
          }

          input.focus();
          input.setSelectionRange(result.caret, result.caret);
        }, 0);
      },
      [text, slashRange, setDraft, resetSlashCommands],
    );

    const onEntitySelect = useCallback(
      entity => {
        if (!entity || !entityRange) {
          return;
        }

        const result = replaceEntityQueryWithAlias({ text, entityRange, entity });

        recordAlias(entity);
        setText(result.text);
        setDraft(result.text);
        resetEntities();

        setTimeout(() => {
          const input = inputRef.current;

          if (!input) {
            return;
          }

          input.focus();
          input.setSelectionRange(result.caret, result.caret);
        }, 0);
      },
      [text, entityRange, setDraft, resetEntities, recordAlias],
    );

    const onSoftLinkifyAccept = useCallback(() => {
      if (!softLinkifyMatch) {
        return;
      }

      const result = replaceEntityQueryWithAlias({
        text,
        entityRange: softLinkifyMatch,
        entity: softLinkifyMatch,
      });

      recordAlias(softLinkifyMatch);
      setText(result.text);
      setDraft(result.text);
      resetSoftLinkify();

      setTimeout(() => {
        const input = inputRef.current;

        if (!input) {
          return;
        }

        input.focus();
        input.setSelectionRange(result.caret, result.caret);
      }, 0);
    }, [text, softLinkifyMatch, setDraft, resetSoftLinkify, recordAlias]);

    const onPaste = useCallback(
      e => {
        const pasted = e.clipboardData?.getData('text') ?? '';
        const reference = resolveEntityPaste(pasted);

        if (!reference) {
          return;
        }

        e.preventDefault();

        recordAlias(reference);

        const input = inputRef.current;
        const selectionStart = input?.selectionStart ?? text.length;
        const selectionEnd = input?.selectionEnd ?? selectionStart;
        const insertion = `${buildEntityAlias(reference.label)} `;
        const nextText = `${text.slice(0, selectionStart)}${insertion}${text.slice(selectionEnd)}`;
        const nextCaret = selectionStart + insertion.length;

        setText(nextText);
        setDraft(nextText);
        resetSoftLinkify();
        notifyTyping(nextText.trim());

        setTimeout(() => {
          const nextInput = inputRef.current;

          if (!nextInput) {
            return;
          }

          nextInput.focus();
          nextInput.setSelectionRange(nextCaret, nextCaret);
        }, 0);
      },
      [text, resolveEntityPaste, setDraft, resetSoftLinkify, notifyTyping, recordAlias],
    );

    const onAttachClick = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    const onFileChange = useCallback(
      e => {
        const file = e.target.files?.[0] ?? null;

        e.target.value = '';

        if (!file || !onUploadAttachment) {
          return;
        }

        if (file.size > TEAM_CHAT_ATTACHMENT_MAX_BYTES) {
          return;
        }

        onUploadAttachment({ file });
      },
      [onUploadAttachment],
    );

    const onShareCurrentViewClick = useCallback(() => {
      const link = getCurrentViewLink();

      if (!link) {
        return;
      }

      setText(previous => {
        const nextText = previous ? `${previous.trimEnd()} ${link}` : link;

        setDraft(nextText);

        return nextText;
      });

      inputRef.current?.focus();
    }, [setDraft]);

    const onSeedEntityReference = useCallback(
      reference => {
        const alias = buildEntityAlias(reference.label);

        recordAlias(reference);

        const nextText = text ? `${text.trimEnd()} ${alias} ` : `${alias} `;

        setText(nextText);
        setDraft(nextText);

        inputRef.current?.focus();
      },
      [text, setDraft, recordAlias],
    );

    useChatEntityAttachment({
      chatId,
      onSeed: onSeedEntityReference,
      candidatesByType: entityCandidatesByType,
      isCandidatesLoaded: isEntityCandidatesLoaded,
    });

    const onSendClick = useCallback(() => {
      const trimmed = text.trim();

      if (!trimmed || isDisabled) {
        return;
      }

      // Aliases are readable UI only — the POSTed body must carry wire tokens (server/AI/badges
      // contract). Raw tokens already in the draft (old drafts, back-compat) pass through untouched.
      const body = convertAliasesToTokens({
        text: trimmed,
        aliasMap: aliasMapRef.current,
        candidatesByType: entityCandidatesByType,
      });

      flushStopTyping();
      onSendMessage({ message: body, replyToMessageId: replyToId });

      aliasMapRef.current = {};
      setText('');
      clearDraft();
      resetMentions();
      resetSlashCommands();
      resetEntities();
      resetSoftLinkify();
    }, [
      text,
      isDisabled,
      onSendMessage,
      flushStopTyping,
      clearDraft,
      replyToId,
      entityCandidatesByType,
      resetMentions,
      resetSlashCommands,
      resetEntities,
      resetSoftLinkify,
    ]);

    const onKeyDown = useCallback(
      e => {
        // The soft-linkify hint only exists when no @/slash/# popup is active (mutual suppression),
        // so its Tab-accept / Esc-dismiss are handled first and never collide with them.
        if (softLinkifyMatch) {
          if (e.keyCode === KEY_CODES.TAB) {
            e.preventDefault();
            onSoftLinkifyAccept();

            return;
          }

          if (e.keyCode === KEY_CODES.ESC) {
            e.preventDefault();
            dismissSoftLinkify();

            return;
          }
        }

        const isMentionPopupVisible = !!mentionRange;
        const isSuggestionsVisible = isMentionPopupVisible && !!suggestions.length;
        const isSlashSuggestionsVisible = !!slashRange && !!slashSuggestions.length;
        const isEntitySuggestionsVisible = !!entityRange && !!entityFlatItems.length;

        if (isEntitySuggestionsVisible) {
          const focusedIndex = entityFlatItems.findIndex(
            item => getEntityReferenceKey(item) === focusedEntityKey,
          );

          if (e.keyCode === KEY_CODES.DOWN) {
            e.preventDefault();
            const nextIndex = Math.min(focusedIndex + 1, entityFlatItems.length - 1);
            setFocusedEntityKey(getEntityReferenceKey(entityFlatItems[nextIndex]));

            return;
          }

          if (e.keyCode === KEY_CODES.UP) {
            e.preventDefault();
            const prevIndex = Math.max(focusedIndex - 1, 0);
            setFocusedEntityKey(getEntityReferenceKey(entityFlatItems[prevIndex]));

            return;
          }

          if (e.keyCode === KEY_CODES.ENTER) {
            e.preventDefault();
            const focused =
              entityFlatItems.find(item => getEntityReferenceKey(item) === focusedEntityKey) ??
              entityFlatItems[0];

            onEntitySelect(focused);

            return;
          }

          if (e.keyCode === KEY_CODES.ESC) {
            e.preventDefault();
            dismissEntities();

            return;
          }
        }

        if (!!entityRange && e.keyCode === KEY_CODES.ESC) {
          e.preventDefault();
          dismissEntities();

          return;
        }

        if (!!entityRange && e.keyCode === KEY_CODES.ENTER) {
          e.preventDefault();
          resetEntities();

          return;
        }

        if (isSlashSuggestionsVisible) {
          const focusedIndex = slashSuggestions.findIndex(
            command => getSlashCommandId(command) === focusedCommandId,
          );

          if (e.keyCode === KEY_CODES.DOWN) {
            e.preventDefault();
            const nextIndex = Math.min(focusedIndex + 1, slashSuggestions.length - 1);
            setFocusedCommandId(getSlashCommandId(slashSuggestions[nextIndex]));

            return;
          }

          if (e.keyCode === KEY_CODES.UP) {
            e.preventDefault();
            const prevIndex = Math.max(focusedIndex - 1, 0);
            setFocusedCommandId(getSlashCommandId(slashSuggestions[prevIndex]));

            return;
          }

          if (e.keyCode === KEY_CODES.ENTER) {
            e.preventDefault();
            const focused =
              slashSuggestions.find(command => getSlashCommandId(command) === focusedCommandId) ??
              slashSuggestions[0];

            onSlashCommandSelect(focused);

            return;
          }

          if (e.keyCode === KEY_CODES.ESC) {
            e.preventDefault();
            resetSlashCommands();

            return;
          }
        }

        if (isSuggestionsVisible) {
          const focusedIndex = suggestions.findIndex(
            member => getMemberId(member) === focusedMemberId,
          );

          if (e.keyCode === KEY_CODES.DOWN) {
            e.preventDefault();
            const nextIndex = Math.min(focusedIndex + 1, suggestions.length - 1);
            setFocusedMemberId(getMemberId(suggestions[nextIndex]));

            return;
          }

          if (e.keyCode === KEY_CODES.UP) {
            e.preventDefault();
            const prevIndex = Math.max(focusedIndex - 1, 0);
            setFocusedMemberId(getMemberId(suggestions[prevIndex]));

            return;
          }

          if (e.keyCode === KEY_CODES.ENTER) {
            e.preventDefault();
            const focused =
              suggestions.find(member => getMemberId(member) === focusedMemberId) ?? suggestions[0];

            onMentionSelect(focused);

            return;
          }

          if (e.keyCode === KEY_CODES.ESC) {
            e.preventDefault();
            resetMentions();

            return;
          }
        }

        if (isMentionPopupVisible && e.keyCode === KEY_CODES.ESC) {
          e.preventDefault();
          resetMentions();

          return;
        }

        if (isMentionPopupVisible && e.keyCode === KEY_CODES.ENTER) {
          e.preventDefault();
          resetMentions();

          return;
        }

        if (e.keyCode === KEY_CODES.ENTER && !e.shiftKey) {
          e.preventDefault();
          onSendClick();
        }
      },
      [
        mentionRange,
        suggestions,
        focusedMemberId,
        slashRange,
        slashSuggestions,
        focusedCommandId,
        entityRange,
        entityFlatItems,
        focusedEntityKey,
        setFocusedCommandId,
        setFocusedMemberId,
        setFocusedEntityKey,
        onMentionSelect,
        onSlashCommandSelect,
        onEntitySelect,
        onSendClick,
        resetMentions,
        resetSlashCommands,
        resetEntities,
        dismissEntities,
        softLinkifyMatch,
        onSoftLinkifyAccept,
        dismissSoftLinkify,
      ],
    );

    return (
      <Box sx={rootStyles}>
        <TeamChatMentionSuggestions
          focusedMemberId={focusedMemberId}
          isHidden={!mentionRange}
          members={suggestions}
          query={mentionQuery}
          onSelectMember={onMentionSelect}
        />
        <TeamChatSlashCommandSuggestions
          commands={slashSuggestions}
          focusedCommandId={focusedCommandId}
          isHidden={!slashRange}
          onSelectCommand={onSlashCommandSelect}
        />
        <TeamChatEntitySuggestions
          focusedEntityKey={focusedEntityKey}
          groups={entityGroups}
          isHidden={!entityRange}
          isLoading={isEntityDataLoading}
          query={entityQuery}
          onSelectEntity={onEntitySelect}
        />
        <TeamChatSoftLinkifyHint
          isHidden={!softLinkifyMatch}
          label={softLinkifyMatch?.label ?? ''}
          onAccept={onSoftLinkifyAccept}
        />
        <TeamChatComposerReferencePreview
          aliasMap={aliasMapRef.current}
          candidatesByType={entityCandidatesByType}
          text={text}
        />
        <Box sx={rowStyles}>
          {!!onUploadAttachment && (
            <IconButton
              aria-label={`Attach file (max ${TEAM_CHAT_ATTACHMENT_MAX_MB}MB)`}
              data-testid={TeamChatMessageInputTestIds.ATTACH_BUTTON}
              disabled={isDisabled}
              size="small"
              onClick={onAttachClick}
            >
              <FileAddIcon size={IconSize[20]} />
            </IconButton>
          )}
          {canShareCurrentView && (
            <Tooltip title={TEAM_CHAT_SHARE_VIEW_TOOLTIP}>
              <span>
                <IconButton
                  aria-label={TEAM_CHAT_SHARE_VIEW_TOOLTIP}
                  data-testid={TeamChatMessageInputTestIds.SHARE_VIEW_BUTTON}
                  disabled={isDisabled}
                  size="small"
                  onClick={onShareCurrentViewClick}
                >
                  <DocLinkIcon size={IconSize[20]} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Box
            accept="*/*"
            component="input"
            ref={fileInputRef}
            sx={hiddenFileInputStyles}
            type="file"
            onChange={onFileChange}
          />
          <TextFieldCDS
            multiline
            dataTestId={TeamChatMessageInputTestIds.MESSAGE_INPUT}
            disabled={isDisabled}
            inputRef={inputRef}
            maxRows={4}
            name="teamChatMessageInput"
            placeholder={placeholder}
            size="small"
            sx={inputStyles}
            value={text}
            onChange={onChange}
            onClick={updateMentionStateFromInputEvent}
            onKeyDown={onKeyDown}
            onKeyUp={updateMentionStateFromInputEvent}
            onPaste={onPaste}
          />
          <Button
            data-testid={TeamChatMessageInputTestIds.SEND_BUTTON}
            disabled={isDisabled || !text.trim()}
            variant="contained"
            onClick={onSendClick}
          >
            Send
          </Button>
        </Box>
      </Box>
    );
  },
);
TeamChatMessageInput.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  chatId: PropTypes.string,
  members: PropTypes.arrayOf(PropTypes.object),
  currentUserId: PropTypes.string,
  priorityMemberIds: PropTypes.arrayOf(PropTypes.string),
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
  canShareCurrentView: PropTypes.bool,
  replyToId: PropTypes.string,
  onUploadAttachment: PropTypes.func,
  onStartTyping: PropTypes.func,
  onStopTyping: PropTypes.func,
};

export default TeamChatMessageInput;
